import React, { useEffect, useState } from "react";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { useFetchBulkScanDetailsMutation } from "../../../features/tenant/bulkScanHistory/bulkScanHistoryApi";
import { toast } from "react-hot-toast";
import { HiOutlineInformationCircle, HiOutlineUser, HiOutlineCalendar, HiOutlineHashtag, HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineClock } from "react-icons/hi";

const BulkScanDetailsModal = ({ show, setShow, scanId }) => {
  const [fetchDetails, { isLoading }] = useFetchBulkScanDetailsMutation();
  const [details, setDetails] = useState(null);

  const getStatusStyle = (status) => {
    switch (status) {
      case "1": // Success
        return "bg-green-100 text-green-800 border-green-200";
      case "0": // Not Found
        return "bg-red-100 text-red-800 border-red-200";
      case "2": // Pending
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "3": // Already Scanned
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "-1": // Already Locked
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "1":
        return <HiOutlineCheckCircle className="w-4 h-4 mr-1" />;
      case "0":
        return <HiOutlineXCircle className="w-4 h-4 mr-1" />;
      case "2":
        return <HiOutlineClock className="w-4 h-4 mr-1" />;
      default:
        return <HiOutlineInformationCircle className="w-4 h-4 mr-1" />;
    }
  };

  useEffect(() => {
    if (show && scanId) {
      handleFetchDetails();
    }
  }, [show, scanId]);

  const handleFetchDetails = async () => {
    try {
      const response = await fetchDetails({
        token: sessionStorage.getItem("access_token"),
        tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        body: { scan_id: scanId },
      }).unwrap();

      if (response.status) {
        setDetails(response.body);
      } else {
        toast.error(response.message || "Failed to fetch details");
      }
    } catch (error) {
      console.error("Fetch details error:", error);
      toast.error(error?.data?.message || "Something went wrong while fetching details");
    }
  };

  return (
    <ModalComponent
      show={show}
      setShow={setShow}
      size="xl"
      title="Bulk Scan Details"
    >
      <div className="p-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-500 font-medium">Loading scan details...</p>
          </div>
        ) : details ? (
          <div className="space-y-8">
            {/* Header Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                <div className="flex items-center text-gray-400 mb-2">
                  <HiOutlineUser className="w-4 h-4 mr-2" />
                  <span className="text-xs font-semibold uppercase tracking-wider">User</span>
                </div>
                <p className="text-gray-900 font-bold">{details.user_name || "N/A"}</p>
                <p className="text-gray-500 text-xs">{details.user_mobile}</p>
              </div>

              <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                <div className="flex items-center text-gray-400 mb-2">
                  <HiOutlineCalendar className="w-4 h-4 mr-2" />
                  <span className="text-xs font-semibold uppercase tracking-wider">Date</span>
                </div>
                <p className="text-gray-900 font-bold">
                  {new Date(details.created_at).toLocaleDateString()}
                </p>
                <p className="text-gray-500 text-xs">
                  {new Date(details.created_at).toLocaleTimeString()}
                </p>
              </div>

              <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                <div className="flex items-center text-gray-400 mb-2">
                  <HiOutlineHashtag className="w-4 h-4 mr-2" />
                  <span className="text-xs font-semibold uppercase tracking-wider">Total Scans</span>
                </div>
                <p className="text-gray-900 text-2xl font-black">{details.count}</p>
              </div>

              <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-sm">
                <div className="flex items-center text-gray-400 mb-2">
                  <HiOutlineInformationCircle className="w-4 h-4 mr-2" />
                  <span className="text-xs font-semibold uppercase tracking-wider">Overall Status</span>
                </div>
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusStyle(details.status)}`}>
                  {getStatusIcon(details.status)}
                  {details.status_text}
                </div>
              </div>
            </div>

            {/* Individual QR Data Table */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="bg-gray-50/50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-sm font-bold text-gray-800">Individual QR Data</h3>
                <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-md border border-gray-100">
                  {details.individual_qr_data?.length || 0} Entries
                </span>
              </div>

              <div className="max-h-[400px] overflow-y-auto overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">QR ID</th>
                      <th className="px-6 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                      <th className="px-6 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Points</th>
                      <th className="px-6 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Product Code</th>
                      <th className="px-6 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {details.individual_qr_data && details.individual_qr_data.length > 0 ? (
                      details.individual_qr_data.map((qr, index) => (
                        <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                            {qr.qr_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-tight border ${getStatusStyle(qr.status)}`}>
                              {qr.status_text}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-sm font-black ${qr.points > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                              {qr.points > 0 ? `+${qr.points}` : qr.points}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {qr.product_code || "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {qr.category_name || "—"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                          <HiOutlineInformationCircle className="w-8 h-8 mx-auto mb-2 opacity-20" />
                          <p>No individual QR data found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                onClick={() => setShow(false)}
                className="px-6 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-md shadow-blue-100 active:scale-95"
              >
                Close Details
              </button>
            </div>
          </div>
        ) : (
          <div className="py-20 text-center text-gray-500">
            <HiOutlineInformationCircle className="w-12 h-12 mx-auto mb-4 opacity-10" />
            <p className="text-lg font-medium">Something went wrong</p>
            <button
              onClick={handleFetchDetails}
              className="mt-4 text-blue-600 font-bold hover:underline"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </ModalComponent>
  );
};

export default BulkScanDetailsModal;
